import { EventApi, EventInput } from '@fullcalendar/core/index.js';
import FullCalendar from '@fullcalendar/react';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DateTime } from 'luxon';
import { EventImpl } from '@fullcalendar/core/internal';
import { DUMMY_EVENT_ID } from '../kitchen-capacity.utils';
import {
  convertEventApiToRecurring,
  convertSetPreparationTimeFormDataToEventSource,
  convertThrottleSettingsToEventInput,
  createPreparationTimeDummyEvent,
  filterPreparationTimeOverlappedEvents,
  recurringEventsToOrderThrottleConfig,
  SetPreparationTimeFormDataProps,
} from './preparation-time.utils';
import { StoreOrderingForm } from 'api/ordering-service/ordering-service.types';
import { useFormContext } from 'react-hook-form';
import { convertNormalHoursToEventInput } from 'components/location-hours/normal-hours-calendar/normal-hours.utils';
import { generateBackgroundEvents } from 'components/location-hours/temporary-hours-calendar/temporary-hours.utils';
import { StandardHoursType } from 'components/location-hours/temporary-hours-calendar/temporary-hours.hooks';

export type PreparationTimeType = {
  preparationTimeRef: MutableRefObject<FullCalendar>;
  preparationTimeEvents: EventInput[];
  backgroundEvents: EventInput[];
  addEvents: (setHourFormData: SetPreparationTimeFormDataProps) => void;
  addDummyEvent: (start: DateTime, end: DateTime) => EventInput;
  removeDummyEvent: () => void;
  eventsSet: (events: EventApi[]) => void;
  handleCalendarViewChange: (view: 'list' | 'week') => void;
  convertEventsToPreparationTime: (
    preparationTimeEvents: EventInput[],
  ) => Partial<StoreOrderingForm>;
  preparationTimeCalendarLoaded: boolean;
  standardHours: StandardHoursType;
};

type UsePreparationTimeProps = {
  storeOrderingData: StoreOrderingForm;
};

// Define custom hook for handling preparation time
export const usePreparationTime = ({
  storeOrderingData,
}: UsePreparationTimeProps) => {
  const { setValue } = useFormContext();
  const [preparationTimeEvents, setPreparationTimeEvents] =
    useState<EventInput[]>();
  const [
    preparationTimeCalendarInitialLoad,
    setPreparationTimeCalendarInitialLoad,
  ] = useState(false);
  const { orderThrottleConfig, pickupOrderHours, deliveryOrderHours } =
    storeOrderingData;

  const preparationTimeRef = useRef<FullCalendar>();
  const preparationTimeCalendarApi = preparationTimeRef.current?.getApi();

  const initialEvents = useMemo(
    () => convertThrottleSettingsToEventInput(orderThrottleConfig),
    [orderThrottleConfig],
  );

  const [isShowingNormalHours, setIsShowingNormalHours] =
    useState<boolean>(false);

  const backgroundEvents = useMemo(() => {
    const locationHoursEvents = [
      ...convertNormalHoursToEventInput(pickupOrderHours, {
        orderType: 'PICKUP',
        eventType: 'LocationHours',
      }),
      ...convertNormalHoursToEventInput(deliveryOrderHours, {
        orderType: 'DELIVERY',
        eventType: 'LocationHours',
      }),
    ];

    return generateBackgroundEvents(locationHoursEvents, isShowingNormalHours);
  }, [pickupOrderHours, deliveryOrderHours, isShowingNormalHours]);

  const convertEventsToPreparationTime = useCallback(
    (preparationTimeEvents: EventInput[]): Partial<StoreOrderingForm> => {
      return {
        orderThrottleConfig: recurringEventsToOrderThrottleConfig(
          preparationTimeEvents,
        ),
      };
    },
    [],
  );

  const addDummyEvent = useCallback(
    (start: DateTime, end: DateTime) => {
      const dummyEvent = createPreparationTimeDummyEvent(start, end);
      preparationTimeCalendarApi?.addEvent(dummyEvent);
      return dummyEvent;
    },
    [preparationTimeCalendarApi],
  );

  const removeDummyEvent = useCallback(() => {
    const dummyEvent = preparationTimeCalendarApi?.getEventById(DUMMY_EVENT_ID);
    if (dummyEvent) {
      dummyEvent.remove();
    }
  }, [preparationTimeCalendarApi]);

  const addEvents = useCallback(
    (setHourFormData: SetPreparationTimeFormDataProps) => {
      const newEvents =
        convertSetPreparationTimeFormDataToEventSource(setHourFormData);

      const overlappedEvents = filterPreparationTimeOverlappedEvents(
        setHourFormData,
        preparationTimeEvents,
      );

      // Remove overlapped events from the calendar
      overlappedEvents.forEach(event => {
        preparationTimeCalendarApi?.getEventById(event.id).remove();
      });

      // Remove existing event if updating
      if (setHourFormData.eventId) {
        preparationTimeCalendarApi
          ?.getEventById(setHourFormData.eventId)
          .remove();
      }

      // Add new events to the calendar
      preparationTimeCalendarApi?.addEventSource(newEvents);
    },
    [preparationTimeEvents, preparationTimeCalendarApi],
  );

  const eventsSet = useCallback((events: EventImpl[]) => {
    // Use reduce to create a map with the last occurrence of each event by unique identifier
    const eventMap = events.reduce((map, event) => {
      map.set(event.id, event);
      return map;
    }, new Map());

    // Convert the map values to an array, filter out events with display = "background",
    // map to the required format, and set the unique events
    setPreparationTimeEvents(
      Array.from(eventMap.values())
        ?.filter(event => event.display !== 'background')
        .map(convertEventApiToRecurring),
    );
  }, []);

  const handleCalendarViewChange = useCallback(
    (view: 'list' | 'week') => {
      switch (view) {
        case 'week':
          preparationTimeCalendarApi?.changeView('timeGridWeek');
          break;
        default:
          preparationTimeCalendarApi?.changeView('listWeek');
      }
    },
    [preparationTimeCalendarApi],
  );

  const removeAllBackgroundEvents = useCallback(() => {
    const allEvents = preparationTimeCalendarApi.getEvents();
    allEvents.forEach(event => {
      if (event.extendedProps.eventType === 'LocationHours') {
        event.remove();
      }
    });
  }, [preparationTimeCalendarApi]);

  useEffect(() => {
    if (preparationTimeCalendarApi && !preparationTimeCalendarInitialLoad) {
      preparationTimeCalendarApi.removeAllEvents();
      preparationTimeCalendarApi.addEventSource(initialEvents);
      setPreparationTimeEvents(initialEvents); // Set initial events to preparationTimeEvents state
      setPreparationTimeCalendarInitialLoad(true); // Set initial load flag to true
    }
  }, [
    initialEvents,
    preparationTimeCalendarApi,
    preparationTimeCalendarInitialLoad,
  ]);

  useEffect(() => {
    setValue(
      'ordering.orderThrottleConfig',
      convertEventsToPreparationTime(preparationTimeEvents).orderThrottleConfig,
    );
  }, [convertEventsToPreparationTime, preparationTimeEvents, setValue]);

  useEffect(() => {
    if (backgroundEvents && preparationTimeCalendarApi) {
      removeAllBackgroundEvents();
      preparationTimeCalendarApi.addEventSource(backgroundEvents);
    }
  }, [backgroundEvents, preparationTimeCalendarApi, removeAllBackgroundEvents]);

  return {
    preparationTimeRef,
    preparationTimeEvents,
    backgroundEvents,
    addEvents,
    eventsSet,
    addDummyEvent,
    removeDummyEvent,
    handleCalendarViewChange,
    convertEventsToPreparationTime,
    preparationTimeCalendarLoaded: preparationTimeCalendarInitialLoad,
    standardHours: {
      isShowingNormalHours,
      setIsShowingNormalHours,
    },
  };
};
